import { useMemo } from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../ui/Pagination';

export default function TablePagination(props: {
  currentPage: number;
  pages: number;
  onPageChange: (page: number) => void;
}) {
  const { currentPage, pages, onPageChange } = props;

  const paginationRange = useMemo(() => {
    const totalPageNumbers = 7; // Show max 7 items: 1 ... 4 5 6 ... 10

    if (totalPageNumbers >= pages) {
      return Array.from({ length: pages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, pages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < pages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = pages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      // Show 1, 2, 3, 4, 5, ... , lastPage
      const leftItemCount = 5;
      const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, '...', lastPageIndex];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      // Show 1, ... , lastPage-4, lastPage-3, lastPage-2, lastPage-1, lastPage
      const rightItemCount = 5;
      const rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => pages - rightItemCount + i + 1
      );
      return [firstPageIndex, '...', ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = Array.from(
        { length: 3 }, // currentPage-1, currentPage, currentPage+1
        (_, i) => leftSiblingIndex + i - 1 // Adjust to get correct range around current page
      );
      // Check if currentPage is near the beginning
      if (currentPage <= 3) {
        middleRange = Array.from({ length: 4 }, (_, i) => i + 1); // Show 1, 2, 3, 4
        return [...middleRange, '...', lastPageIndex];
      }
      // Check if currentPage is near the end
      if (currentPage >= pages - 2) {
        middleRange = Array.from({ length: 4 }, (_, i) => pages - 3 + i); // Show last 4 pages
        return [firstPageIndex, '...', ...middleRange];
      }

      // Default middle range case
      middleRange = Array.from({ length: 3 }, (_, i) => currentPage - 1 + i);
      return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
    }

    // Should not happen if logic is correct, but fallback
    return Array.from({ length: pages }, (_, i) => i + 1);
  }, [pages, currentPage]);

  if (pages <= 1) {
    return null; // Don't show pagination if only one page
  }

  const onNext = () => {
    if (currentPage < pages) {
      onPageChange(currentPage + 1);
    }
  };

  const onPrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  return (
    <Pagination className="mt-4">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#" // Use href for consistency, prevent default in onClick
            onClick={(e) => {
              e.preventDefault();
              if (currentPage !== 1) {
                onPrevious();
              }
            }}
            aria-disabled={currentPage === 1}
            className={
              currentPage === 1 ? 'pointer-events-none opacity-50' : ''
            }
          />
        </PaginationItem>
        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === '...') {
            return <PaginationEllipsis key={`ellipsis-${index}`} />;
          }
          return (
            <PaginationItem key={pageNumber}>
              <PaginationLink
                href="#" // Prevent page reload
                onClick={(e) => {
                  e.preventDefault(); // Prevent default anchor behavior
                  onPageChange(pageNumber as number);
                }}
                isActive={currentPage === pageNumber}
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          );
        })}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage !== pages) {
                onNext();
              }
            }}
            aria-disabled={currentPage === pages}
            className={
              currentPage === pages ? 'pointer-events-none opacity-50' : ''
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
